import type { Card } from '@/features/card/api/types';
import type { Parking, ParkingType } from '@/features/parking/api/types';
import type { SearchType } from '@/features/parking/types';
import type { Coupon } from '@/features/coupon/api/types';
import type { PaymentMethodType } from '@/features/card/constants';
import type { PointUsageValue } from '@/features/yumenavi/constants/point';
import { PARKING_TYPE } from '~/features/monthly/constants/parkingType';

type StoredParkingsOnLists = {
  parkings: Parking[];
  searchType: SearchType | undefined;
  page: number;
  parkingType: ParkingType;
};

type PaymentMethod = {
  paymentAmount?: number;
  methodType?: PaymentMethodType;
  card?: Card;
  coupon?: Coupon;
  yumenaviPointUsage?: PointUsageValue;
  yumenaviPoints?: number;
};

export const useParkingState = () => {
  /**
   * API取得した駐車場履歴一覧
   * NOTE: どのページ分まで取得できているか判定するためpageも保持
   */
  const storedParkingsOnLists = useState<StoredParkingsOnLists>(
    'storedParkingsOnLists',
    () => ({
      parkings: [],
      searchType: undefined,
      page: 1,
      parkingType: PARKING_TYPE.NORMAL,
    }),
  );

  /**
   * 検索条件の説明
   * @example この付近,東京都新宿区,...
   */
  const searchDescription = useState<string>('searchDescription', () => '');

  /**
   * 駐車場タイプ(normal/monthly)のタブ切り替え状態
   */
  const parkingType = useState<ParkingType>('searchType', () => 'normal');

  /**
   * 選択された支払い方法
   */
  const paymentMethod = useState<PaymentMethod | undefined>(
    'paymentMethod',
    () => undefined,
  );

  const setStoredParkings = (_parkings: Parking[]) => {
    storedParkingsOnLists.value.parkings = _parkings;
  };

  const setStoredSearchType = (_searchType: SearchType | undefined) => {
    storedParkingsOnLists.value.searchType = _searchType;
  };

  const setStoredParkingType = (_parkingType: ParkingType) => {
    parkingType.value = _parkingType;
  };

  const setStoredPage = (_page: number) => {
    storedParkingsOnLists.value.page = _page;
  };

  const resetStoredParkings = () => {
    storedParkingsOnLists.value.parkings = [];
    storedParkingsOnLists.value.page = 1;
  };

  const setSearchDescription = (_description: string) => {
    searchDescription.value = _description;
  };

  const setPaymentMethod = (_paymentMethod: PaymentMethod | undefined) => {
    paymentMethod.value = _paymentMethod;
  };

  const resetAll = () => {
    resetStoredParkings();
    setStoredSearchType(undefined);
    setSearchDescription('');
    setPaymentMethod(undefined);
  };

  return {
    storedParkingsOnLists: storedParkingsOnLists,
    paymentMethod: readonly(paymentMethod),
    searchDescription: readonly(searchDescription),
    parkingType: readonly(parkingType),
    setStoredParkings,
    setStoredSearchType,
    setStoredParkingType,
    setStoredPage,
    resetStoredParkings,
    setSearchDescription,
    setPaymentMethod,
    resetAll,
  };
};
